<template>
  <div class="d-flex flex-column align-items-center large-top-margin">
    <h1 class="text-secondary" :style="{ fontSize: subHeadlineFontSize * 1.35 + 'px' }">{{ headline }}</h1>
    <h2 class="text-secondary" :style="{ fontSize: subHeadlineFontSize * 0.85 + 'px' }">{{ subHeadline }}</h2>
    <div v-if="!mfaPending" class="login-fields mt-3">
      <div class="input-group mb-3">
        <span class="input-group-text custom-text"><i class="fas fa-envelope" style="color: #b0b0b0;"></i></span>
        <input type="email" placeholder="Email Address" class="form-control" v-model="email" autocomplete="username webauthn" />
      </div>
      <div v-if="!passkeyMode" class="input-group mb-3">
        <span class="input-group-text custom-text" @click="passwordVisible = !passwordVisible">
          <i :class="passwordVisible ? 'fas fa-eye-slash' : 'fas fa-eye'" style="color: #b0b0b0;"></i>
        </span>
        <input :type="passwordVisible ? 'text' : 'password'" placeholder="Password" class="form-control" v-model="password" autocomplete="current-password" />
      </div>
    </div>
    <form v-if="mfaPending" class="login-fields mt-3" @submit.prevent="verifyCode">
      <label for="mfa-code">Enter the six-digit code from Google Authenticator</label>
      <select v-if="factors.length > 1" v-model="factorId" aria-label="Authenticator" class="form-control mb-3">
        <option v-for="factor in factors" :key="factor.uid" :value="factor.uid">{{ factor.displayName || 'Authenticator' }}</option>
      </select>
      <input id="mfa-code" v-model="code" class="form-control" inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="[0-9]{6}" required />
      <button class="btn custom-button mt-3 active" :disabled="isSubmitting || !/^[0-9]{6}$/.test(code)">Verify code</button>
      <button type="button" class="btn mt-3" :disabled="isSubmitting" @click="cancelMfa">Back to sign in</button>
    </form>
    <div v-if="!mfaPending && passkeyMode" class="login-actions mt-3">
      <button class="btn custom-button active" :disabled="isSubmitting" @click="usePasskey(false)">{{ isSubmitting ? 'Signing in…' : 'Sign in with passkey' }}</button>
      <button class="btn" :disabled="isSubmitting" @click="usePassword">Use password and authenticator</button>
    </div>
    <button v-if="!mfaPending && !passkeyMode && passkeyAvailable" class="btn mt-3" :disabled="isSubmitting" @click="passkeyMode = true; usePasskey(false)">Use passkey instead</button>
    <p v-if="errorMessage" class="login-error" role="alert">{{ errorMessage }}</p>
    <p v-if="/^llf/i.test(password)" role="alert">Check your welcome email to create your password before signing in.</p>
    <button v-if="!mfaPending && !passkeyMode" class="btn custom-button mt-3" :class="{ active: isValidForm }" :disabled="!isValidForm || isSubmitting" @click="handleLogin">Submit</button>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { getMultiFactorResolver, TotpMultiFactorGenerator } from 'firebase/auth';
import { projectAuth } from '@/firebase/firebase';
import { passkeysSupported, passkeyAutofillSupported, cancelPasskey, signInWithAdminPasskey } from '@/security/passkeySignIn';
import { signInWithEmail } from '@/firebase/auth';

export default {
  name: 'LogInMenu',
  props: {
    headline: { type: String, required: true },
    subHeadline: { type: String, required: true },
    subHeadlineFontSize: { type: Number, required: true },
    adminMode: { type: Boolean, required: true }
  },
  emits: ['close', 'loginSuccess'],
  setup(props, { emit }) {
    const email = ref('');
    const password = ref('');
    const passwordVisible = ref(false);
    const isSubmitting = ref(false);
    const router = useRouter();
    const mfaPending = ref(false);
    const code = ref('');
    const errorMessage = ref('');
    const factors = ref([]);
    const factorId = ref('');
    const passkeyAvailable = passkeysSupported();
    const passkeyMode = ref(passkeyAvailable);
    let attempt = 0;
    let disposed = false;
    const usePassword = () => { attempt++; cancelPasskey(); passkeyMode.value = false; errorMessage.value = ''; };
    const usePasskey = async autofill => {
      if (isSubmitting.value || disposed) return;
      const current = ++attempt;
      cancelPasskey(); errorMessage.value = '';
      if (!autofill) isSubmitting.value = true;
      try {
        const uid = await signInWithAdminPasskey(autofill, () => !disposed && current === attempt && passkeyMode.value, () => { isSubmitting.value = true; });
        if (uid && !disposed && current === attempt) { emit('loginSuccess', uid); await router.push('/admin-db'); }
      } catch (error) {
        if (disposed || current !== attempt) return;
        if (!autofill) {
          passkeyMode.value = false;
          errorMessage.value = error.code === 'functions/internal'
            ? 'The server could not complete passkey sign-in. Use password and authenticator for now, or contact your administrator.'
            : 'Passkey sign-in was canceled or unavailable. Use password and authenticator, or retry your passkey.';
        }
      } finally { if (current === attempt) isSubmitting.value = false; }
    };
    onMounted(async () => {
      if (!passkeyAvailable) return;
      try { if (await passkeyAutofillSupported() && !disposed && passkeyMode.value) void usePasskey(true); } catch { /* Explicit passkey button remains available. */ }
    });
    onBeforeUnmount(() => { disposed = true; attempt++; cancelPasskey(); });
    let resolver = null;
    const cancelMfa = () => {
      resolver = null; mfaPending.value = false; code.value = ''; password.value = '';
      factors.value = []; factorId.value = ''; errorMessage.value = '';
    };
    const verifyCode = async () => {
      if (!resolver || isSubmitting.value || !/^[0-9]{6}$/.test(code.value)) return;
      isSubmitting.value = true;
      errorMessage.value = '';
      try {
        const assertion = TotpMultiFactorGenerator.assertionForSignIn(factorId.value, code.value);
        const credential = await resolver.resolveSignIn(assertion);
        cancelMfa();
        emit('loginSuccess', credential.user.uid);
        await router.push('/admin-db');
      } catch (error) {
        code.value = '';
        if (['auth/invalid-multi-factor-session', 'auth/multi-factor-session-expired'].includes(error.code)) {
          cancelMfa();
          errorMessage.value = 'Your sign-in expired. Please start again.';
        } else {
          errorMessage.value = 'Unable to verify. Check the current code and try again, or return to sign in.';
        }
      } finally { isSubmitting.value = false; }
    };
    const isValidForm = computed(() => /^[^\s@]+@thelomaxlawfirm\.com$/i.test(email.value.trim()) && password.value.length >= 5 && !/^llf/i.test(password.value));
    const handleLogin = async () => {
      if (!isValidForm.value || isSubmitting.value) { return; }
      isSubmitting.value = true;
      errorMessage.value = '';
      try {
        const user = await signInWithEmail(email.value.trim(), password.value);
        emit('loginSuccess', user);
        await router.push('/admin-db');
      } catch (error) {
        password.value = '';
        if (error.code === 'auth/multi-factor-auth-required') {
          resolver = getMultiFactorResolver(projectAuth, error);
          factors.value = resolver.hints.filter(factor => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID);
          if (factors.value.length) {
            factorId.value = factors.value[0].uid;
            mfaPending.value = true;
          } else {
            resolver = null;
            errorMessage.value = 'This account needs a different authentication method. Contact your administrator.';
          }
        } else {
          errorMessage.value = 'Unable to sign in. Please check your email and password.';
        }
      } finally {
        isSubmitting.value = false;
      }
    };
    return { passkeyAvailable, passkeyMode, usePassword, usePasskey, mfaPending, code, errorMessage, factors, factorId, verifyCode, cancelMfa, email, password, passwordVisible, isSubmitting, isValidForm, handleLogin };
  }
};
</script>

<style scoped>
.login-error {
  color: #b42318;
  width: min(420px, calc(100% - 32px));
  margin: 16px 0 0;
  text-align: center;
}
.login-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: min(360px, calc(100% - 32px));
  text-align: center;
}
.login-actions .btn {
  width: 100%;
  white-space: normal;
}
</style>
